
import filter from "lodash-es/filter";
import map from "lodash-es/map";
import lodashGet from "lodash-es/get";
import head from "lodash-es/head";
import sortBy from "lodash-es/sortBy";
// import flatten from "lodash-es/flatten";
// import flatMapDeep from "lodash-es/flatMapDeep";

import { Memoize, Debounce } from "lodash-decorators";
// import Memoize from "lodash-decorators/Memoize";
// import Debounce from "lodash-decorators/Debounce";

import "es6-promise";

import axios, { AxiosResponse } from "axios";
import Vue, { ComponentOptions } from "vue";
import Component from "vue-class-component";
import {
  Emit,
  Inject,
  Model,
  Prop,
  Provide,
  Watch,
} from "vue-property-decorator";

import { State, Getter, Action, Mutation, namespace } from "vuex-class";

import { ComputeJob, LaxyFile } from "../model";
import { WebAPI } from "../web-api";
import { FETCH_FILESET } from "../store";
import { strToRegex } from "../util";
import {
  hasSharedTagOrEmpty,
  hasIntersection,
  filterByTag,
  excludeByTag,
  filterByRegex,
  filterByFullPath,
  viewFile,
  downloadFile,
  fileListToTree,
  flattenTree,
  TreeNode,
  findPair,
} from "../file-tree-util";

import { DummyFileSet as _dummyFileSet } from "../test-data";
import { Snackbar } from "../snackbar";
import { ViewMethod } from "../types";

@Component({
  filters: {},
})
export default class NestedFileList extends Vue {
  _DEBUG: boolean = false;

  @Prop()
  public fileTree: TreeNode<LaxyFile>; // TreeNode<any>; ?

  @Prop({ type: String, default: "" })
  public rootPathName: string;

  @Prop(String)
  public title: string;

  @Prop({
    type: Array,
    default: () => {
      return [];
    },
  })
  public regexFilters: string[];

  @Prop({
    type: Array,
    default: () => {
      return [];
    },
  })
  public tagFilters: string[];

  @Prop({
    type: Array,
    default: () => {
      return [];
    },
  })
  public excludeTags: string[];

  @Prop({ default: () => ["file"], type: Array })
  public selectableTypes: string[];

  @Prop({ default: false, type: Boolean })
  public singleSelect: boolean;

  @Prop({ default: true, type: Boolean })
  public hideSearch: boolean;

  @Prop({ default: false, type: Boolean })
  public hideActions: boolean;

  @Prop({ default: false, type: Boolean })
  public showBackArrow: boolean;

  @Prop({ default: true, type: Boolean })
  public autoSelectPair: boolean;

  @Prop({ default: 2, type: Number })
  public minQueryLength: number;

  @Prop(String)
  public jobId: string | null;

  @Prop({ default: "Search", type: String })
  public searchBoxPlaceholder: string;

  public searchQuery: string = "";
  public searching: boolean = false;

  public actionRunning: any = {};

  // for templates
  getProp = lodashGet;

  get selectedFiles(): LaxyFile[] {
    return (this.$refs["file-table"] as MdTable).selectedRows as LaxyFile[];
  }

  @Watch("fileTree")
  initCurrentLevel(new_val: TreeNode<LaxyFile>, old_value: TreeNode<LaxyFile>) {
    this.currentLevel = this.fileTree;
  }

  public currentLevel: TreeNode<LaxyFile> | null = null;

  @Debounce(600)
  get searchFilteredNodes(): Array<TreeNode<LaxyFile>> {
    this.searching = true;
    const nodes = flattenTree(this.fileTree.children);

    if (!this.searchQuery || this.searchQuery.trim().length === 0) return nodes;
    const query = this.searchQuery.trim().toLowerCase();

    const hits: Array<TreeNode<LaxyFile>> = filter(
      nodes,
      (node: TreeNode<LaxyFile>) => {
        if (node.obj) {
          return `${node.obj.name}/${node.obj.name}`
            .toLowerCase()
            .includes(query);
          // TODO: Make globbing work, or look at vuex-search
          // return minimatch(`${node.obj.name}/${node.obj.name}`, query);
        } else {
          return node.name.toLowerCase().includes(query);
          // TODO: Make globbing work, or look at vuex-search
          // return minimatch(node.name, query);
        }
      }
    );
    this.searching = false;
    return hits;
  }

  get currentLevelNodes(): Array<TreeNode<LaxyFile>> {
    if (this.currentLevel) {
      let nodes = this.currentLevel.children;
      const query = this.searchQuery.trim();
      if (query.length >= this.minQueryLength) {
        nodes = this.searchFilteredNodes;
      }
      return sortBy(nodes, [
        (n: TreeNode<LaxyFile>) => n.obj != null,
        "meta.type",
        "name",
      ]);
    }
    return [];
  }

  get pathToRoot(): TreeNode<LaxyFile>[] {
    if (this.currentLevel) {
      let parent = this.currentLevel.parent;
      let nodes: TreeNode<LaxyFile>[] = [this.currentLevel];
      while (parent != null) {
        if (parent.name) nodes.push(parent);
        parent = parent.parent;
      }
      nodes.reverse();
      return nodes;
    }
    return [];
  }

  /*
  get fileTree(): TreeNode<LaxyFile> {
      if (this.files) {
          return fileListToTree(this.files);
      } else {
          return this._emptyTreeRoot;
      }
  }
  */

  get currentLevelFiles(): (LaxyFile | null)[] {
    if (this.currentLevel) {
      return map(
        sortBy(this.currentLevel.children, ["name"]),
        (node) => node.obj
      );
    }
    return [];
  }

  upDirectory(): TreeNode<LaxyFile> | null {
    if (this.currentLevel && this.currentLevel.parent) {
      this.currentLevel = this.currentLevel.parent;
    }
    this.searchQuery = "";
    return this.currentLevel;
  }

  enterDirectory(node: TreeNode<LaxyFile>) {
    this.searchQuery = "";
    this.currentLevel = node;
  }

  onSelect(rows: any) {
    this.$emit("select", rows);
    // console.dir(rows);
  }

  // checks / unchecks the mdTableRow checkbox, based on finding the
  // rows associated (file) object
  _setRowsCheckboxState(files: LaxyFile[], state: boolean): void {
    const table = this.$refs["file-table"] as MdTable;
    // skip header row(s)
    const rows = filter(table.$children, (r) => !r.headRow);
    for (let file of files) {
      const row = rows[table.data.indexOf(file)];
      table.setRowSelection(state, file);
      row.checkbox = state;
    }

    // we need to re-emit the md-table @select event since we've auto-selected rows
    // (MdTable.setRowSelection doesn't do this itself)
    this.$emit("select", table.selectedRows);
  }

  onSelectedRow(file: LaxyFile) {
    if (this.singleSelect && this.currentLevelFiles != null) {
      this._setRowsCheckboxState(this.currentLevelFiles as LaxyFile[], false);
      this._setRowsCheckboxState([file], true);
    }

    if (this.autoSelectPair && this.files && file) {
      const pair = findPair(file, this.files);
      if (pair != null) {
        this._setRowsCheckboxState([pair], true);
      }
      // console.log([file, pair]);
    }

    this.$emit("selected", file);
  }

  onDeselectedRow(file: LaxyFile) {
    if (this.autoSelectPair && this.files && file) {
      const pair = findPair(file, this.files);
      if (pair != null) {
        this._setRowsCheckboxState([pair], false);
      }
      // console.log([file, pair]);
    }
    this.$emit("deselected", file);
  }

  public isInCart(file: LaxyFile) {
    for (let sample of this.$store.state.samples.items) {
      if (sample.files.includes(file)) return true;
    }
    return false;
  }

  public shouldDisableCheckbox(file: LaxyFile) {
    return this.isInCart(file) || file.deleted === true;
  }

  private viewMethods: ViewMethod[] = [
    {
      text: "Open in new tab",
      icon: "open_in_new",
      tags: [],
      method: (file: LaxyFile) => {
        viewFile(file, null, this.jobId);
      },
    },
    {
      text: "Download file",
      icon: "cloud_download",
      tags: [],
      method: (file: LaxyFile) => {
        downloadFile(file, null, this.jobId);
      },
    },
    {
      text: "View report",
      icon: "remove_red_eye",
      tags: ["html", "report"],
      method: (file: LaxyFile) => {
        viewFile(file, null, this.jobId);
      },
    },
    {
      text: "Open in Degust",
      icon: "dashboard",
      tags: ["counts", "degust"],
      method: async (file: LaxyFile) => {
        window.open(
          WebAPI.getExternalAppRedirectUrl("degust", file.id),
          "_blank"
        );
      },
    },
  ];

  public refreshing: boolean = false;

  mounted() {
    // this.fileList = _dummyFileSet.files;
    // this.filesetId = _dummyFileSet.id;
    //this.currentLevel = this.fileTree[4];
    this.currentLevel = this.fileTree;
  }

  // we need this wrapped in a method otherwise the viewMethod.method
  // doesn't have the correct 'this' context to $emit events.
  emitActionError(msg: string) {
    this.$emit("action-error", msg);
  }

  getViewMethodsForTags(tags: string[]) {
    return filter(this.viewMethods, (vm) => hasSharedTagOrEmpty(vm.tags, tags));
  }

  @Memoize((file: LaxyFile) => file.id)
  getDefaultViewMethod(file: LaxyFile) {
    return head(
      filter(this.viewMethods, (vm) => hasIntersection(vm.tags, file.type_tags))
    );
  }

  get files(): LaxyFile[] {
    const nodelist = flattenTree(this.fileTree.children);
    let filtered: LaxyFile[] = map(nodelist, (n) => n.obj as LaxyFile);
    filtered = filterByTag(filtered, this.tagFilters);
    filtered = filterByRegex(filtered, strToRegex(this.regexFilters));
    filtered = excludeByTag(filtered, this.excludeTags)
    return filtered;
  }

  get titleText(): string {
    return this.title;
  }
}
